<!--
  @TradeDesk  Main area of the page "Handeln" (master view): wish list (Merkliste) of the hero selected in
              panels/MasterTradePanel.svelte. Merchant items can be customized and sold, found items are passed.
-->

<script>
		import {user, send_data} from "../client.svelte.js";
		import {protoShopitems, gearTypes, gear_type, money_label, perksToString} from "../core.js";
		import {marked_entries} from "./Shop.svelte";
		import {desk} from "../panels/MasterTradePanel.svelte";

		let hero = $derived(user.characters.get(desk.heroID))
		let entries = $derived(marked_entries(hero))
		let groups = $derived(Object.groupBy(entries, entry => entry.source))
		let cash = $derived(
				(user.possessions.get(`${hero?.ID}$gold`)?.amount ?? 0) * 100 +
				(user.possessions.get(`${hero?.ID}$silver`)?.amount ?? 0) )
		let openTotal = $derived((groups.merchant ?? []).reduce((sum, entry) => sum + full_price(entry), 0))
		let firstName = $derived(hero?.name.split(" ")[0] ?? "")

		const shopItemIDs = new Set(protoShopitems.map(item => item.ID))

		let editing = $state(null)		// ID of the possession whose item is edited
		let draft = $state({})
		let dones = $state([])			// items sold or passed in this view: {heroID, source, name, label}
		let failure = $state("")

		const handovers = [
				{source: 'found', title: "Fundstücke", hint: "Kostenlos – nur übergeben.", empty: "Keine Fundstücke vorgemerkt."},
				{source: 'companion', title: "Von Gefährten", hint: "Bezahlung regeln die Gefährten selbst – nur übergeben.",
				 empty: "Keine Angebote von Gefährten vorgemerkt."} ]

		function full_price({poss, price}) {
				return price * (poss.delta || 1)
		}

		function weight_label(weight) {
				return (weight) ? `${weight.toLocaleString('de-DE', {maximumFractionDigits: 1})} kg` : "–"
		}

		function perks_label(perks) {
				const perkStr = perksToString(perks)
				return (perkStr === "–") ? "" : perkStr
		}

		/** perks as editable text, e.g. "cmb +1, Feuer -1" (parsed by the server) **/
		function perks_text(perks) {
				return Object.entries(perks ?? {})
						.map(([key, val]) => (typeof val === 'number') ? `${key} ${(val > 0) ? "+" : ""}${val}` : `${key} ${val}`)
						.join(", ")
		}

		function start_edit({poss, item, price}) {
				draft = {name: item.name, descript: item.descript ?? "", grade: +item.grade || 0, weight: item.weight ?? 0,
				         specials: item.specials ?? "", perks: perks_text(item.perks), gold: Math.floor(price / 100), silver: price % 100}
				editing = poss.ID
		}

		async function save_edit(poss) {
				const changes = {name: draft.name, descript: draft.descript, specials: draft.specials, perks: draft.perks,
				                 grade: Math.max(0, Math.round(+draft.grade || 0)), weight: Math.max(0, +draft.weight || 0),
				                 value: Math.max(0, Math.round(+draft.gold || 0)) * 100 + Math.max(0, Math.round(+draft.silver || 0))}
				await request(`possession/${poss.ID}/item`, changes, 'PATCH')
				editing = null
		}

		/** sells (merchant) or passes (found, companion) a marked item to the hero **/
		async function hand_over({poss, item, source}, label) {
				await request(`trade/${hero.ID}`, {possession: poss.ID}, 'POST')
				dones.push({heroID: hero.ID, source, name: item.name, label})
				if (editing === poss.ID)  editing = null
		}

		async function request(url, data, method) {
				failure = ""
				try {
						await send_data(url, data, method)
				} catch (error) {
						failure = error.message
						throw error
				}
		}
</script>


<!-------------------------------- HTML -------------------------------------->

<section class="desk">
	{#if hero}
		<div class="head">
			<div>
				<p class="kicker">Merkliste von</p>
				<h1>{hero.name}</h1>
			</div>
			<div class="stats">
				<div class="stat"><span class="label">Bargeld</span><span class="value">{money_label(cash)}</span></div>
				<div class="stat"><span class="label">Händlerwaren offen</span><span class="value">{money_label(openTotal)}</span></div>
				<div class="stat"><span class="label">Danach</span><span class="value" class:negative={cash < openTotal}>{money_label(cash - openTotal)}</span></div>
			</div>
		</div>

		{#if failure}<p class="failure" role="alert">{failure}</p>{/if}

		<section class="group" aria-labelledby="desk-merchant">
			<div class="grouphead">
				<h2 id="desk-merchant">Vom Händler</h2>
				<span class="hint">Vor dem Verkauf anpassbar – z. B. Boni oder Zauber verleihen. Der Spieler sieht den geänderten Preis.</span>
			</div>
			{@render doneRows('merchant')}
			{#if !groups.merchant?.length}
				<p class="empty">Keine Händlerwaren vorgemerkt.</p>
			{/if}
			<ul>
				{#each groups.merchant ?? [] as entry (entry.poss.ID)}
					{@const custom = !shopItemIDs.has(entry.item.ID)}
					<li class="entry" class:editing={editing === entry.poss.ID}>
						{#if editing === entry.poss.ID}
							{@render editForm(entry, custom)}
						{:else}
							{@render merchantView(entry, custom)}
						{/if}
					</li>
				{/each}
			</ul>
		</section>

		{#each handovers as {source, title, hint, empty}}
			<section class="group" aria-label={title}>
				<div class="grouphead">
					<h2>{title}</h2>
					<span class="hint">{hint}</span>
				</div>
				{@render doneRows(source)}
				{#if !groups[source]?.length}
					<p class="empty">{empty}</p>
				{/if}
				<ul>
					{#each groups[source] ?? [] as entry (entry.poss.ID)}
						<li class="entry">
							<div class="itemrow">
								{@render itemInfo(entry.item, (source === 'found') ? "Fundstück der Gemeinschaft" : "Angebot eines Gefährten", source)}
								<button class="primary pass" onclick={() => hand_over(entry, `An ${hero.name} übergeben · Besitz als erhalten markiert`)}>
									An {firstName} übergeben <i class="fa icon-arrow-right"></i>
								</button>
							</div>
						</li>
					{/each}
				</ul>
			</section>
		{/each}
	{/if}
</section>


{#snippet doneRows(source)}
	{#each dones.filter(done => done.heroID === hero.ID && done.source === source) as done}
		<div class="done">
			<span class="check" aria-hidden="true"><i class="fa icon-check"></i></span>
			<span class="donename">{done.name}</span>
			<span class="donelabel">{done.label}</span>
		</div>
	{/each}
{/snippet}

{#snippet itemInfo(item, origin, source)}
	{@const type = gear_type(item)}
	{#if item.imgpath}
		<img class="tile" src={item.imgpath} alt={item.name}>
	{:else}
		<div class="tile noimg">
			<i class="fa icon-{gearTypes[type].icon}"></i>
			<span>kein Bild</span>
		</div>
	{/if}
	<div class="info">
		<div class="title">
			<h3>{item.name}</h3>
			{#if source === 'merchant'}
				{@const custom = !shopItemIDs.has(item.ID)}
				<span class="tag" class:custom>{(custom) ? `Eigener Gegenstand ${item.ID}` : "Ladenartikel"}</span>
			{:else}
				<span class="tag">{gearTypes[type].name}</span>
			{/if}
		</div>
		{#if item.descript}<p class="descript">{item.descript}</p>{/if}
		<p class="origin {source}">{origin}</p>
	</div>
	<dl class="attribs">
		<dt>Stufe</dt>
		<dt>Gewicht</dt>
		<dt>Besonderheiten</dt>
		<dd class="grade">{+item.grade || "–"}</dd>
		<dd>{weight_label(item.weight)}</dd>
		<dd class="specials">{item.specials || "–"}</dd>
	</dl>
{/snippet}

{#snippet merchantView(entry, custom)}
	{@const price = full_price(entry)}
	{@const perks = perks_label(entry.item.perks)}
	<div class="itemrow">
		{@render itemInfo(entry.item, (custom) ? "Für den Helden angepasster Gegenstand · die Vormerkung verweist auf diesen Gegenstand"
		                                      : "Allgemeiner Ladenartikel des Händlers", 'merchant')}
		<div class="price">
			<span class="label">Preis für Spieler</span>
			<span class="value">{money_label(price)}</span>
			{#if entry.poss.delta > 1}<span class="label">{entry.poss.delta} Stück</span>{/if}
		</div>
	</div>
	{#if perks}
		<div class="perks"><span class="label">Boni & Zauber</span><span>{perks}</span></div>
	{/if}
	<div class="actions">
		{#if price > cash}
			<span class="toodear">Bargeld von {hero.name} reicht nicht.</span>
		{/if}
		<button class="secondary" onclick={() => start_edit(entry)}><i class="fa icon-pen"></i>Bearbeiten</button>
		<button class="primary" disabled={price > cash}
		        onclick={() => hand_over(entry, `Verkauft an ${hero.name} für ${money_label(price)} · Besitz als erhalten markiert, Bargeld abgezogen`)}>
			An {firstName} verkaufen · {money_label(price)}
		</button>
	</div>
{/snippet}

{#snippet editForm(entry, custom)}
	{@const id = entry.poss.ID}
	<div class="edithead">
		<h3>{(custom) ? "Eigenen Gegenstand bearbeiten" : "Ladenartikel anpassen"}</h3>
		<span class="tag" class:custom>{(custom) ? `Eigener Gegenstand ${entry.item.ID}` : "Ladenartikel"}</span>
	</div>
	<p class="notice">
		{(custom) ? `Änderungen werden direkt am eigenen Gegenstand ${entry.item.ID} gespeichert.`
		          : `Beim Speichern wird aus dem Ladenartikel „${entry.item.name}“ ein eigener Gegenstand für ${hero.name} erzeugt. Die Vormerkung verweist danach auf diesen Gegenstand; der Ladenartikel bleibt unverändert.`}
	</p>
	<div class="form">
		<div class="field span3"><label for="{id}-name">Name</label><input id="{id}-name" bind:value={draft.name}></div>
		<div class="field span3"><label for="{id}-descript">Aussehen</label><input id="{id}-descript" bind:value={draft.descript}></div>
		<div class="field span1"><label for="{id}-grade">Stufe</label><input id="{id}-grade" type="number" min="0" bind:value={draft.grade}></div>
		<div class="field span1"><label for="{id}-weight">Gewicht (kg)</label><input id="{id}-weight" type="number" min="0" step="0.1" bind:value={draft.weight}></div>
		<div class="field span4"><label for="{id}-specials">Besonderheiten</label><input id="{id}-specials" bind:value={draft.specials}></div>
		<div class="field span4">
			<label for="{id}-perks">Boni & Zauber (Boni in-App)</label>
			<textarea id="{id}-perks" rows="3" bind:value={draft.perks} placeholder="z. B. cmb +1, impact +2"></textarea>
		</div>
		<div class="field span2">
			<span class="labeltext" id="{id}-price">Preis für Spieler</span>
			<div class="money" role="group" aria-labelledby="{id}-price">
				<input aria-label="Gold" type="number" min="0" bind:value={draft.gold}><span>GS</span>
				<input aria-label="Silber" type="number" min="0" max="99" bind:value={draft.silver}><span>SM</span>
			</div>
		</div>
	</div>
	<div class="actions">
		<button class="secondary" onclick={() => editing = null}>Abbrechen</button>
		<button class="primary" disabled={!draft.name} onclick={() => save_edit(entry.poss)}>
			{(custom) ? "Speichern" : "Eigenen Gegenstand erzeugen"}
		</button>
	</div>
{/snippet}


<!-------------------------------- STYLE -------------------------------------->

<style lang="sass">
	@use "../colors" as colors

	.desk
		grid-area: main
		display: flex
		flex-direction: column
		gap: 20px
		min-width: 0
		max-height: calc(100vh - 96px)
		padding: 20px
		overflow-y: auto
		scrollbar-width: thin
		border: 1px solid colors.$grey-20
		border-radius: 10px
		background: #1b1a1a

	.kicker
		margin: 0
		font-size: 12px
		letter-spacing: .08em
		text-transform: uppercase
		color: colors.$primary

	h1, h2, h3
		margin: 0
		font-family: var(--font-heading, "EB Garamond", Georgia, serif)

	h1
		font-size: 34px
		line-height: 1.1

	h2
		font-size: 22px

	h3
		font-size: 20px
		line-height: 1.15

	.label, .labeltext
		font-size: 12px
		letter-spacing: .06em
		text-transform: uppercase
		color: colors.$grey-60

	/* — header with cash statistics — */
	.head
		display: flex
		flex-wrap: wrap
		align-items: flex-end
		justify-content: space-between
		gap: 16px
		padding-bottom: 14px
		border-bottom: 1px solid colors.$grey-20

	.stats
		display: flex
		flex-wrap: wrap
		gap: 10px

	.stat
		display: flex
		flex-direction: column
		padding: 6px 12px
		border: 1px solid colors.$grey-20
		border-radius: 8px
		background: colors.$grey-15
		line-height: 1.2

		.label
			font-size: 11px
			letter-spacing: .08em

		.value
			font-family: var(--font-heading, "EB Garamond", Georgia, serif)
			font-size: 20px
			font-weight: bold

			&.negative
				color: #ff8a8a

	.failure
		margin: 0
		padding: 10px 12px
		border: 1px solid colors.$highlight-red-100
		border-radius: 8px
		background: colors.$highlight-red-040-black
		font-size: 14px

	/* — groups of items by source — */
	.group
		display: flex
		flex-direction: column
		gap: 10px

		ul
			display: flex
			flex-direction: column
			gap: 10px
			margin: 0
			padding: 0
			list-style: none

	.grouphead
		display: flex
		flex-wrap: wrap
		align-items: baseline
		gap: 10px

	.hint
		font-size: 13px
		color: colors.$grey-60

	.empty
		margin: 0
		padding: 16px
		border: 1px dashed colors.$grey-30
		border-radius: 10px
		font-size: 14px
		color: colors.$grey-60

	.done
		display: flex
		flex-wrap: wrap
		align-items: center
		gap: 12px
		padding: 14px 16px
		border: 1px solid colors.$grey-20
		border-radius: 10px
		background: #161616

		.check
			width: 32px
			height: 32px
			display: grid
			place-items: center
			border-radius: 50%
			border: 1px solid colors.$primary-100
			background: colors.$primary-040-black
			color: colors.$primary

		.donename
			font-family: var(--font-heading, "EB Garamond", Georgia, serif)
			font-size: 18px
			font-weight: bold

		.donelabel
			flex: 1
			min-width: 200px
			font-size: 14px
			color: colors.$grey-85

	.entry
		display: flex
		flex-direction: column
		gap: 12px
		padding: 14px 16px
		border: 1px solid colors.$grey-20
		border-radius: 10px
		background: colors.$grey-15

		&.editing
			border-color: colors.$highlight-yellow-100

	.itemrow
		display: flex
		flex-wrap: wrap
		align-items: center
		gap: 14px 20px

	.tile
		flex: none
		width: 96px
		height: 96px
		border-radius: 8px
		object-fit: cover

		&.noimg
			display: flex
			flex-direction: column
			align-items: center
			justify-content: center
			gap: 6px
			border: 1px dashed colors.$grey-30
			color: colors.$grey-50

			.fa
				font-size: 28px

			span
				font-size: 11px
				color: colors.$grey-60

	.info
		flex: 1 1 240px
		min-width: 0
		display: flex
		flex-direction: column
		gap: 5px

	.title, .edithead
		display: flex
		flex-wrap: wrap
		align-items: center
		gap: 8px

	.tag
		flex: none
		padding: 1px 9px
		border-radius: 20px
		font-size: 12px
		white-space: nowrap
		border: 1px solid colors.$grey-30
		background: colors.$grey-20
		color: colors.$grey-85

		&.custom
			border-color: colors.$highlight-blue-100
			background: colors.$highlight-blue-040-black
			color: colors.$white

	.descript
		margin: 0
		font-size: 14px
		color: colors.$grey-85

	.origin
		margin: 0
		font-size: 13px
		color: colors.$grey-60

		&.found
			color: colors.$primary

		&.companion
			color: colors.$highlight-yellow-100

	.attribs
		flex: 1 1 300px
		min-width: 0
		margin: 0
		display: grid
		grid-template-columns: 48px 70px minmax(0, 1fr)
		gap: 2px 16px
		align-items: baseline

		dt
			font-size: 12px
			letter-spacing: .06em
			text-transform: uppercase
			color: colors.$grey-60

		dd
			margin: 0
			font-size: 15px

		.grade
			font-family: var(--font-heading, "EB Garamond", Georgia, serif)
			font-size: 20px
			font-weight: bold

		.specials
			font-size: 14px
			color: colors.$grey-85

	.price
		flex: none
		display: flex
		flex-direction: column
		align-items: flex-end
		margin-left: auto
		line-height: 1.2

		.value
			font-family: var(--font-heading, "EB Garamond", Georgia, serif)
			font-size: 22px
			font-weight: bold
			white-space: nowrap

	.perks
		display: flex
		gap: 10px
		align-items: baseline
		padding: 8px 12px
		border: 1px solid colors.$highlight-blue-100
		border-radius: 8px
		background: colors.$highlight-blue-040-black
		font-size: 14px

		.label
			color: colors.$white

	/* — buttons — */
	.actions
		display: flex
		flex-wrap: wrap
		align-items: center
		justify-content: flex-end
		gap: 10px
		padding-top: 12px
		border-top: 1px solid colors.$grey-20

	.toodear
		margin-right: auto
		font-size: 13px
		color: #ff8a8a

	.primary, .secondary
		display: flex
		align-items: center
		gap: 8px
		min-height: 44px
		margin: 0
		padding: 0 18px
		border-radius: 8px
		font-size: 14px
		font-family: inherit
		cursor: pointer

		&:focus-visible
			outline: 2px solid colors.$primary
			outline-offset: 2px

	.primary
		border: 1px solid colors.$primary-100
		background: colors.$primary-100
		color: colors.$white
		font-weight: bold

		&:disabled
			border-color: colors.$grey-30
			background: colors.$grey-20
			color: colors.$grey-60
			cursor: not-allowed

	.secondary
		border: 1px solid colors.$grey-50
		background: transparent
		color: colors.$white

	.pass
		flex: none
		margin-left: auto

	/* — edit form — */
	.notice
		margin: 0
		padding: 10px 12px
		border: 1px solid colors.$highlight-yellow-100
		border-radius: 8px
		background: colors.$highlight-yellow-040-black
		font-size: 14px

	.form
		display: grid
		grid-template-columns: repeat(6, minmax(0, 1fr))
		gap: 12px

	.field
		display: flex
		flex-direction: column
		gap: 3px
		min-width: 0

		label
			font-size: 12px
			color: colors.$grey-60

		.labeltext
			letter-spacing: 0
			text-transform: none

		input, textarea
			box-sizing: border-box
			width: 100%
			margin: 0
			padding: 0 10px
			border: 1px solid colors.$grey-30
			border-radius: 5px
			background: colors.$black
			color: colors.$white
			font-size: 15px
			font-family: inherit

			&:focus-visible
				outline: 2px solid colors.$primary
				outline-offset: 2px

		input
			height: 40px

		textarea
			padding: 8px 10px
			line-height: 1.4
			resize: vertical

	.span1
		grid-column: span 1
	.span2
		grid-column: span 2
	.span3
		grid-column: span 3
	.span4
		grid-column: span 4

	.money
		display: flex
		align-items: center
		gap: 6px

		input
			flex: 1
			width: 0
			text-align: right

		span
			font-size: 13px
			color: colors.$grey-85
</style>
